// The issue's relations, as today's issue page shows them: grouped under
// Blocks, Blocked by, Related, Duplicate of and Duplicated by, each the
// other issue's line. Unlike today's page, a person adds and removes them
// here: `relate_issues` and `unrelate_issues`, through the store's outbox,
// so they show at once and wait out a lost connection.
import { useId, useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { Issue, Relation, RelationKind } from '../api/types'
import { CloseIcon, PlusIcon, PriorityIcon, StateIcon } from '../components/Icons'
import { sameKey } from '../lib/keys'
import { issuePath } from '../plans/html'
import { useBoardState, useStore } from '../store/context'
import { RELATION_KINDS } from './relations'

export function IssueRelations({ issue }: { issue: Issue }) {
  const store = useStore()
  const { issues } = useBoardState()
  const [adding, setAdding] = useState(false)
  const relations = issue.relations ?? []
  const groups = RELATION_KINDS.map(({ kind, name }) => ({ kind, name, relations: relations.filter((r) => r.kind === kind) })).filter((g) => g.relations.length > 0)
  return (
    <section className="section issue-relations" aria-label="Relations">
      <header className="section-header">
        <h2>
          Relations
          {relations.length > 0 && <span className="section-count">{relations.length}</span>}
        </h2>
        {!adding && (
          <button type="button" className="button button-small button-quiet" onClick={() => setAdding(true)}>
            <PlusIcon size={12} /> Add relation
          </button>
        )}
      </header>
      {adding && <AddRelation issue={issue} onDone={() => setAdding(false)} />}
      {groups.length === 0 && !adding && <p className="muted">No relations yet.</p>}
      {groups.map((group) => (
        <div key={group.kind} className="relation-group">
          <h3>{group.name}</h3>
          <ul className="line-list">
            {group.relations.map((relation) => (
              <li key={relation.issue} className="relation-line">
                <RelatedLine relation={relation} other={issues.get(relation.issue)} />
                <button
                  type="button"
                  className="relation-remove"
                  aria-label={`Remove the relation to ${relation.key}`}
                  title="Remove the relation"
                  onClick={() => void store.unrelateIssues(issue.id, relation.kind, relation.issue)}
                >
                  <CloseIcon size={12} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  )
}

function RelatedLine({ relation, other }: { relation: Relation; other: Issue | undefined }) {
  if (!other) {
    return (
      <span className="issue-line">
        <span className="issue-line-key">{relation.key}</span>
      </span>
    )
  }
  return (
    <Link className="issue-line" to={issuePath(other)}>
      <StateIcon state={other.state} labelled />
      <span className="issue-line-key">{other.key}</span>
      <span className="issue-line-title">{other.title}</span>
      <PriorityIcon priority={other.priority} />
    </Link>
  )
}

/** The kind, and the other issue by its key, in any spelling; the store's issues offered as the person types. */
function AddRelation({ issue, onDone }: { issue: Issue; onDone: () => void }) {
  const store = useStore()
  const { issues } = useBoardState()
  const [kind, setKind] = useState<RelationKind>('related')
  const [typed, setTyped] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const listId = useId()
  const others = useMemo(() => [...issues.values()].filter((i) => i.id !== issue.id).sort((a, b) => a.key.localeCompare(b.key)), [issues, issue.id])
  const submit = (event: FormEvent) => {
    event.preventDefault()
    const text = typed.trim()
    if (!text) return
    if (sameKey(text, issue.key)) {
      setProblem('An issue cannot relate to itself.')
      return
    }
    const other = others.find((i) => sameKey(i.key, text))
    if (!other) {
      setProblem(`No issue ${text} on the board.`)
      return
    }
    void store.relateIssues(issue.id, kind, other.id)
    onDone()
  }
  return (
    <form className="relation-add" onSubmit={submit} aria-label="Add relation">
      <select aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value as RelationKind)}>
        {RELATION_KINDS.map((k) => (
          <option key={k.kind} value={k.kind}>
            {k.name}
          </option>
        ))}
      </select>
      <input
        aria-label="Issue"
        placeholder="Issue key, such as Poulpe-012"
        list={listId}
        value={typed}
        autoFocus
        onChange={(e) => {
          setTyped(e.target.value)
          setProblem(null)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            onDone()
          }
        }}
      />
      <datalist id={listId}>
        {others.map((other) => (
          <option key={other.id} value={other.key}>
            {other.title}
          </option>
        ))}
      </datalist>
      <button type="submit" className="button button-small" disabled={!typed.trim()}>
        Add
      </button>
      <button type="button" className="button button-small button-quiet" onClick={onDone}>
        Cancel
      </button>
      {problem && (
        <p className="relation-problem" role="alert">
          {problem}
        </p>
      )}
    </form>
  )
}
